import React,{useState,useEffect,useRef}from'react';
import api from'../../services/api';

interface Props{
  kind:'patients'|'doctors'|'employees'|'corporate'|'departments';
  label:string;
  placeholder?:string;
  picked?:any;
  display?:string;
  onPick:(row:any)=>void;
  onClear?:()=>void;
  onFreeText?:(text:string)=>void;
  emptyHint?:string;
}

const ICONS:Record<string,string>={patients:'👤',doctors:'👨‍⚕️',employees:'👥',corporate:'🏢',departments:'🏥'};

function labelOf(kind:string,r:any){
  if(kind==='patients') return `${r.first_name||r.firstName||''} ${r.last_name||r.lastName||''}`.trim()||r.uhid;
  if(kind==='doctors') return `Dr. ${r.first_name||r.firstName||''} ${r.last_name||r.lastName||''}`.trim();
  if(kind==='employees') return `${r.first_name||r.firstName||''} ${r.last_name||r.lastName||''}`.trim();
  return r.name||r.provider_name||r.account_name||r.department_name||'—';
}
function subOf(kind:string,r:any){
  if(kind==='patients') return [r.uhid,r.phone,r.crm_id||r.crmId].filter(Boolean).join(' · ');
  if(kind==='doctors') return [r.specialization,r.designation].filter(Boolean).join(' · ');
  if(kind==='employees') return [r.role,r.designation,r.employee_id||r.employeeId].filter(Boolean).join(' · ');
  return r.provider_type||'';
}

export default function EntityLookup({kind,label,placeholder,picked,display,onPick,onClear,onFreeText,emptyHint}:Props){
  const[q,setQ]=useState(display||'');
  const[rows,setRows]=useState<any[]>([]);
  const[show,setShow]=useState(false);
  const[loading,setLoading]=useState(false);
  const[noneInSystem,setNoneInSystem]=useState(false);
  const ref=useRef<HTMLDivElement>(null);

  useEffect(()=>{setQ(display||'');},[display]);
  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setShow(false);};
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[]);

  useEffect(()=>{
    if(!show) return;
    let cancelled=false;
    const t=setTimeout(async()=>{
      setLoading(true);
      try{
        const r=await api.crmLookup(kind,q);
        if(cancelled) return;
        const list=Array.isArray(r?.data)?r.data:r?.data?.data||[];
        setRows(list);
        if(!q) setNoneInSystem(list.length===0);
      }catch{if(!cancelled){setRows([]);setNoneInSystem(true);}}
      if(!cancelled) setLoading(false);
    }, q?280:0);
    return()=>{cancelled=true;clearTimeout(t);};
  },[q,kind,show]);

  const pick=(r:any)=>{setQ(labelOf(kind,r));setShow(false);onPick(r);};
  const clear=()=>{setQ('');onClear?.();onFreeText?.('');};

  if(picked){
    return(
      <div>
        <label className="label">{label}</label>
        <div className="flex items-center gap-2 p-2.5 rounded-xl border-2 border-[#02357A]/30 bg-blue-50">
          <span>{ICONS[kind]}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{labelOf(kind,picked)}</p>
            <p className="text-[11px] text-slate-500 truncate">{subOf(kind,picked)}</p>
          </div>
          <button type="button" className="text-xs text-slate-500 hover:text-red-600" onClick={clear}>Change</button>
        </div>
      </div>
    );
  }

  return(
    <div ref={ref} className="relative">
      <label className="label">{label}</label>
      <input className="input" value={q} placeholder={placeholder||`Search ${kind} or type if none`}
        onFocus={()=>setShow(true)}
        onChange={e=>{setQ(e.target.value);onFreeText?.(e.target.value);}}/>
      {show&&(
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-xl max-h-56 overflow-y-auto">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50 border-b">
            {loading?'Searching…':noneInSystem&&!q?'No records in system — type to fill':`${rows.length} in system`}
          </div>
          {rows.map((r:any)=>(
            <button type="button" key={r.id||labelOf(kind,r)} onClick={()=>pick(r)}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-0">
              <p className="text-sm font-medium">{ICONS[kind]} {labelOf(kind,r)}</p>
              <p className="text-[11px] text-slate-400">{subOf(kind,r)}</p>
            </button>
          ))}
          {!loading&&rows.length===0&&(
            <p className="p-3 text-xs text-slate-500">{emptyHint||'Nothing matched. Keep typing — value will be saved as entered.'}</p>
          )}
        </div>
      )}
    </div>
  );
}
